import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInbox,
    faTruck,
    faCheckCircle,
    faFlask,
    faDatabase,
    faChartPie,
    faChartLine,
    faCalendarDay,
    faCalendarWeek,
    faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import './StatusChart.css';

const StatusChart = ({ samples, filteredSamples }) => {
    const [statusCounts, setStatusCounts] = useState({});
    const [filteredStatusCounts, setFilteredStatusCounts] = useState({});
    const [view, setView] = useState('overview');
    const [timeframeView, setTimeframeView] = useState('daily');
    

    // Calculate status counts for all samples
    useEffect(() => {
        if (!samples || samples.length === 0) return;
        
        const counts = {};
        const statusOrder = ['Order Received', 'Sent Cargo', 'Cargo Delivered', 'Lab Received', 'Data Delivered'];
        
        // Initialize all statuses to 0
        statusOrder.forEach(status => {
            counts[status] = 0;
        });
        
        // Count samples by status
        samples.forEach(patient => {
            const status = patient.status.currentStage;
            counts[status] = (counts[status] || 0) + 1;
        });
        
        setStatusCounts(counts);
    }, [samples]);

    // Calculate status counts for filtered samples
    useEffect(() => {
        if (!filteredSamples || filteredSamples.length === 0) {
            setFilteredStatusCounts({});
            return;
        }
        
        const counts = {};
        const statusOrder = ['Order Received', 'Sent Cargo', 'Cargo Delivered', 'Lab Received', 'Data Delivered'];
        
        // Initialize all statuses to 0
        statusOrder.forEach(status => {
            counts[status] = 0;
        });
        
        // Count filtered samples by status
        filteredSamples.forEach(patient => {
            const status = patient.status.currentStage;
            counts[status] = (counts[status] || 0) + 1;
        });
        
        setFilteredStatusCounts(counts);
    }, [filteredSamples]);

    // Calculate total samples
    const totalSamples = Object.values(statusCounts).reduce((total, count) => total + count, 0);
    const totalFilteredSamples = Object.values(filteredStatusCounts).reduce((total, count) => total + count, 0);
    
    // Check if filters are applied
    const isFiltered = filteredSamples && filteredSamples.length !== samples.length;

    // Helper function to format dates consistently
    const formatDate = (date) => {
        return date.toISOString().split('T')[0];
    };

    // Calculate counts for completed samples by timeframe
    const calculateCompletedCounts = () => {
        if (!samples || samples.length === 0) return { today: 0, week: 0, month: 0 };
        
        const now = new Date();
        const today = formatDate(now);
        
        // Calculate one week ago
        const oneWeekAgo = new Date(now);
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        // Calculate one month ago
        const oneMonthAgo = new Date(now);
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        let todayCount = 0;
        let weekCount = 0;
        let monthCount = 0;
        
        samples.forEach(sample => {
            // Check if sample has 'Data delivered' stage completed
            if (sample.status.stages['Data Delivered']) {
                const completionDate = new Date(sample.status.stages['Data Delivered']);
                
                // Count for today
                if (formatDate(completionDate) === today) {
                    todayCount++;
                }
                
                // Count for week
                if (completionDate >= oneWeekAgo) {
                    weekCount++;
                }
                
                // Count for month
                if (completionDate >= oneMonthAgo) {
                    monthCount++;
                }
            }
        });
        
        return { today: todayCount, week: weekCount, month: monthCount };
    };

    const completedCounts = calculateCompletedCounts();

    // Calculate the time data based on the selected timeframe
    const calculateTimeData = () => {
        const now = new Date();
        let periods = [];
        let periodData = [];

        if (timeframeView === 'daily') {
            // Last 7 days
            for (let i = 6; i >= 0; i--) {
                const date = new Date(now);
                date.setDate(date.getDate() - i);
                const formattedDate = formatDate(date);
                periods.push({
                    label: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : date.toLocaleDateString('default', { weekday: 'short' }),
                    date: formattedDate
                });
            }
        } else if (timeframeView === 'weekly') {
            // Last 4 weeks
            for (let i = 3; i >= 0; i--) {
                const weekStart = new Date(now);
                weekStart.setDate(weekStart.getDate() - (weekStart.getDay() + 7 * i));
                
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);
                
                periods.push({
                    label: i === 0 ? 'This week' : `Week -${i}`,
                    startDate: formatDate(weekStart),
                    endDate: formatDate(weekEnd)
                });
            }
        } else if (timeframeView === 'monthly') {
            // Last 6 months
            for (let i = 5; i >= 0; i--) {
                const date = new Date(now);
                date.setMonth(date.getMonth() - i);
                
                const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
                const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                
                periods.push({
                    label: i === 0 ? 'This month' : date.toLocaleDateString('default', { month: 'short' }),
                    startDate: formatDate(monthStart),
                    endDate: formatDate(monthEnd)
                });
            }
        }

        // Calculate completed and received counts for each period
        periodData = periods.map(period => {
            let completedCount = 0;
            let receivedCount = 0;
            
            samples.forEach(sample => {
                const receivedDate = sample.status.stages['Order Received'] ? new Date(sample.status.stages['Order Received']) : null;
                const completedDate = sample.status.stages['Data Delivered'] ? new Date(sample.status.stages['Data Delivered']) : null;
                
                if (timeframeView === 'daily') {
                    if (receivedDate && formatDate(receivedDate) === period.date) {
                        receivedCount++;
                    }
                    if (completedDate && formatDate(completedDate) === period.date) {
                        completedCount++;
                    }
                } else {
                    // For weekly and monthly
                    const periodStart = new Date(period.startDate);
                    const periodEnd = new Date(period.endDate);
                    
                    if (receivedDate && receivedDate >= periodStart && receivedDate <= periodEnd) {
                        receivedCount++;
                    }
                    if (completedDate && completedDate >= periodStart && completedDate <= periodEnd) {
                        completedCount++;
                    }
                }
            });
            
            return {
                ...period,
                completedCount,
                receivedCount
            };
        });

        return periodData;
    };

    const timeData = calculateTimeData();
    
    // Helper function to render a circular gauge
    const renderGauge = (value, total, label, color = '#206cb9b2', size = 150) => {
        const percentage = total > 0 ? (value / total) * 100 : 0;
        const strokeWidth = size * 0.08;
        const radius = (size - strokeWidth) / 2;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (percentage / 100) * circumference;
        const centerX = size / 2;
        const centerY = size / 2;

        return (
            <div className="samplemanager-gauge-container">
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    {/* Background circle */}
                    <circle
                        cx={centerX}
                        cy={centerY}
                        r={radius}
                        fill="transparent"
                        stroke="#e7e7e785"
                        strokeWidth={strokeWidth}
                    />
                    {/* Foreground circle */}
                    <circle
                        cx={centerX}
                        cy={centerY}
                        r={radius}
                        fill="transparent"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${centerX} ${centerY})`}
                    />
                    {/* Percentage text */}
                    <text
                        x={centerX}
                        y={centerY - 5}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={size * 0.2}
                        fontWeight="bold"
                        fill={color}
                    >
                        {Math.round(percentage)}%
                    </text>
                    {/* Value text */}
                    <text
                        x={centerX}
                        y={centerY + 20}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={size * 0.1}
                        fill="#555"
                    >
                        {value} / {total}
                    </text>
                </svg>
                <div className="samplemanager-gauge-label">{label}</div>
            </div>
        );
    };

    return (
        <div className="samplemanager-status-chart">
            <div className="samplemanager-status-chart-header">
                <div className="samplemanager-status-chart-title1">Sample Analytics</div>
                <div className="samplemanager-status-chart-tabs">
                    <button 
                        className={`samplemanager-status-chart-tab ${view === 'overview' ? 'active' : ''}`}
                        onClick={() => setView('overview')}
                    >
                        <FontAwesomeIcon icon={faChartPie} />
                        <span>Overview</span>
                    </button>
                    <button 
                        className={`samplemanager-status-chart-tab ${view === 'trends' ? 'active' : ''}`}
                        onClick={() => setView('trends')}
                    >
                        <FontAwesomeIcon icon={faChartLine} />
                        <span>Performance</span>
                    </button>
                </div>
            </div>
            
            {view === 'overview' && (
                <div className="samplemanager-status-chart-container">
                    <div className="samplemanager-status-gauges">
                        {renderGauge(
                            isFiltered ? totalFilteredSamples : totalSamples,
                            totalSamples,
                            'Total Samples'
                        )}
                        
                        {/* Status-specific gauges */}
                        <div className="samplemanager-status-small-gauges">
                            {Object.entries(isFiltered ? filteredStatusCounts : statusCounts).map(([status, count]) => {
                                if (count === 0) return null;
                                
                                const totalToUse = isFiltered ? totalFilteredSamples : totalSamples;
                                return renderGauge(count, totalToUse, status, '#206cb9b2', 100);
                            })}
                        </div>
                    </div>
                    
                    <div className="samplemanager-completion-stats">
                        <h4 className="samplemanager-completion-title">Completion Statistics</h4>
                        <div className="samplemanager-completion-cards">
                            <div className="samplemanager-completion-card">
                                <div className="samplemanager-completion-value">{completedCounts.today}</div>
                                <div className="samplemanager-completion-label">Completed Today</div>
                            </div>
                            <div className="samplemanager-completion-card">
                                <div className="samplemanager-completion-value">{completedCounts.week}</div>
                                <div className="samplemanager-completion-label">Completed This Week</div>
                            </div>
                            <div className="samplemanager-completion-card">
                                <div className="samplemanager-completion-value">{completedCounts.month}</div>
                                <div className="samplemanager-completion-label">Completed This Month</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {view === 'trends' && (
                <div className="samplemanager-trends-container">
                    <div className="samplemanager-trends-controls">
                        <button 
                            className={`samplemanager-trends-control ${timeframeView === 'daily' ? 'active' : ''}`}
                            onClick={() => setTimeframeView('daily')}
                        >
                            <FontAwesomeIcon icon={faCalendarDay} />
                            <span>Daily</span>
                        </button>
                        <button 
                            className={`samplemanager-trends-control ${timeframeView === 'weekly' ? 'active' : ''}`}
                            onClick={() => setTimeframeView('weekly')}
                        >
                            <FontAwesomeIcon icon={faCalendarWeek} />
                            <span>Weekly</span>
                        </button>
                        <button 
                            className={`samplemanager-trends-control ${timeframeView === 'monthly' ? 'active' : ''}`}
                            onClick={() => setTimeframeView('monthly')}
                        >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <span>Monthly</span>
                        </button>
                    </div>
                    
                    <div className="samplemanager-trends-title">
                        {timeframeView === 'daily' && 'Last 7 Days'}
                        {timeframeView === 'weekly' && 'Last 4 Weeks'}
                        {timeframeView === 'monthly' && 'Last 6 Months'}
                    </div>
                    
                    <div className="samplemanager-trends-chart">
                        {timeData.map((period, index) => (
                            <div key={index} className="samplemanager-trend-column">
                                <div className="samplemanager-trend-bars">
                                    <div className="samplemanager-trend-bar-container">
                                        <div 
                                            className="samplemanager-trend-bar samplemanager-trend-completed"
                                            style={{ height: `${Math.min(100, (period.completedCount / (Math.max(...timeData.map(d => d.completedCount)) || 1)) * 100)}%` }}
                                        ></div>
                                        <div className="samplemanager-trend-bar-value">{period.completedCount}</div>
                                    </div>
                                    <div className="samplemanager-trend-bar-container">
                                        <div 
                                            className="samplemanager-trend-bar samplemanager-trend-received"
                                            style={{ height: `${Math.min(100, (period.receivedCount / (Math.max(...timeData.map(d => d.receivedCount)) || 1)) * 100)}%` }}
                                        ></div>
                                        <div className="samplemanager-trend-bar-value">{period.receivedCount}</div>
                                    </div>
                                </div>
                                <div className="samplemanager-trend-label">{period.label}</div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="samplemanager-trends-legend">
                        <div className="samplemanager-trends-legend-item">
                            <div className="samplemanager-trends-legend-color samplemanager-trends-legend-completed"></div>
                            <span>Completed</span>
                        </div>
                        <div className="samplemanager-trends-legend-item">
                            <div className="samplemanager-trends-legend-color samplemanager-trends-legend-received"></div>
                            <span>New Orders</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatusChart;
